import { useCallback, useMemo, useState } from 'react';
import {
  provisionStaffCanvasGrants,
  revokeStaffCanvasGrants,
} from '../../services/lms-provisioning.service';
import {
  CANVAS_ACCESS_BULK_MODE,
  CANVAS_ACCESS_BULK_SAME_ROLE_DRAFT_KEY,
} from '../../constants/canvas-access.constants';
import {
  applyBulkSameRoleDraft,
  buildBulkRemoveConfirmMessage,
  buildCanvasAccessBulkGrants,
  buildRevokeLocationKeys,
  getCommonRoleIntersection,
  getProvisionedSelectedRows,
  getRevokeFailureMessage,
  getSelectedCanvasAccessRows,
  hasAllBulkDraftRoles,
  normalizeCanvasRoleId,
} from '../utils/canvas-access.utils';

/**
 * Manages bulk Canvas role selection, provisioning, and remove for the Canvas Access tab.
 * @param {{
 *   personId: string|number|null,
 *   staffName?: string|null,
 *   locations: import('../utils/canvas-access.utils').CanvasAccessLocationRow[],
 *   roleOptionsByLocation: Record<string|number, import('../utils/canvas-access.utils').CanvasRoleOption[]>,
 *   loadRolesForLocation: (locationKey: string|number) => Promise<import('../utils/canvas-access.utils').CanvasRoleOption[]>,
 *   refresh: () => Promise<unknown>,
 *   onClearRowEdit: () => void,
 *   onSetSubmitting: (value: boolean) => void,
 *   onSetError: (error: Error|null) => void,
 * }} params
 */
const useStaffCanvasAccessBulkBarHook = ({
  personId,
  staffName = null,
  locations,
  roleOptionsByLocation,
  loadRolesForLocation,
  refresh,
  onClearRowEdit,
  onSetSubmitting,
  onSetError,
}) => {
  const [bulkMode, setBulkMode] = useState(CANVAS_ACCESS_BULK_MODE.IDLE);
  const [selectedLocationKeys, setSelectedLocationKeys] = useState([]);
  const [draftRoleByLocation, setDraftRoleByLocation] = useState({});
  const [rolesLoading, setRolesLoading] = useState(false);
  const [bulkRemoveOpen, setBulkRemoveOpen] = useState(false);

  const selectedRows = useMemo(
    () => getSelectedCanvasAccessRows(locations, selectedLocationKeys),
    [locations, selectedLocationKeys],
  );

  const provisionedSelectedRows = useMemo(
    () => getProvisionedSelectedRows(locations, selectedLocationKeys),
    [locations, selectedLocationKeys],
  );

  const commonRoleIntersection = useMemo(
    () => getCommonRoleIntersection(selectedLocationKeys, roleOptionsByLocation),
    [roleOptionsByLocation, selectedLocationKeys],
  );

  const resetBulkState = useCallback(() => {
    setBulkMode(CANVAS_ACCESS_BULK_MODE.IDLE);
    setSelectedLocationKeys([]);
    setBulkRemoveOpen(false);
  }, []);

  const toggleSelected = useCallback((locationKey) => {
    setSelectedLocationKeys((prev) =>
      prev.includes(locationKey) ? prev.filter((key) => key !== locationKey) : [...prev, locationKey],
    );
  }, []);

  const startBulkSameRole = useCallback(async () => {
    onClearRowEdit();
    setBulkRemoveOpen(false);
    setBulkMode(CANVAS_ACCESS_BULK_MODE.SAME_ROLE);
    setRolesLoading(true);
    try {
      await Promise.all(selectedLocationKeys.map((locationKey) => loadRolesForLocation(locationKey)));
    } finally {
      setRolesLoading(false);
    }
  }, [loadRolesForLocation, onClearRowEdit, selectedLocationKeys]);

  const startBulkDifferentRoles = useCallback(async () => {
    onClearRowEdit();
    setBulkRemoveOpen(false);
    setBulkMode(CANVAS_ACCESS_BULK_MODE.DIFFERENT_ROLES);

    const nextDraft = {};
    selectedRows.forEach((row) => {
      nextDraft[row.locationKey] = row.canvasRoleId ?? null;
    });
    setDraftRoleByLocation((prev) => ({ ...prev, ...nextDraft }));
    setRolesLoading(true);
    try {
      await Promise.all(selectedLocationKeys.map((locationKey) => loadRolesForLocation(locationKey)));
    } finally {
      setRolesLoading(false);
    }
  }, [loadRolesForLocation, onClearRowEdit, selectedLocationKeys, selectedRows]);

  const cancelBulk = useCallback(() => {
    setBulkMode(CANVAS_ACCESS_BULK_MODE.IDLE);
    setDraftRoleByLocation({});
    setBulkRemoveOpen(false);
  }, []);

  const handleBulkSameRoleChange = useCallback((event) => {
    const roleId = normalizeCanvasRoleId(event.target.value);
    setDraftRoleByLocation((prev) =>
      applyBulkSameRoleDraft(
        prev,
        selectedLocationKeys,
        roleId,
        CANVAS_ACCESS_BULK_SAME_ROLE_DRAFT_KEY,
      ),
    );
  }, [selectedLocationKeys]);

  const handleBulkDifferentRoleChange = useCallback((locationKey, value) => {
    setDraftRoleByLocation((prev) => ({
      ...prev,
      [locationKey]: normalizeCanvasRoleId(value),
    }));
  }, []);

  const saveBulk = useCallback(async () => {
    const grants = buildCanvasAccessBulkGrants(
      selectedLocationKeys,
      draftRoleByLocation,
      roleOptionsByLocation,
    );
    if (grants.length === 0) {
      return;
    }

    onSetSubmitting(true);
    onSetError(null);
    try {
      await provisionStaffCanvasGrants(personId, grants);
      resetBulkState();
      await refresh();
    } catch (err) {
      onSetError(err);
      throw err;
    } finally {
      onSetSubmitting(false);
    }
  }, [
    draftRoleByLocation,
    onSetError,
    onSetSubmitting,
    personId,
    refresh,
    resetBulkState,
    roleOptionsByLocation,
    selectedLocationKeys,
  ]);

  const requestBulkRemove = useCallback(() => {
    if (provisionedSelectedRows.length === 0) {
      return;
    }

    onClearRowEdit();
    onSetError(null);
    setBulkRemoveOpen(true);
  }, [onClearRowEdit, onSetError, provisionedSelectedRows.length]);

  const cancelBulkRemove = useCallback(() => {
    setBulkRemoveOpen(false);
  }, []);

  const confirmBulkRemove = useCallback(async () => {
    const locationKeys = buildRevokeLocationKeys(provisionedSelectedRows);
    if (locationKeys.length === 0) {
      return;
    }

    onSetSubmitting(true);
    onSetError(null);
    try {
      const response = await revokeStaffCanvasGrants(personId, locationKeys);
      const partialMessage = getRevokeFailureMessage(response);
      resetBulkState();
      await refresh();
      if (partialMessage) {
        onSetError(new Error(partialMessage));
      }
    } catch (err) {
      onSetError(err);
      throw err;
    } finally {
      onSetSubmitting(false);
    }
  }, [
    onSetError,
    onSetSubmitting,
    personId,
    provisionedSelectedRows,
    refresh,
    resetBulkState,
  ]);

  const clearBulkForRowEdit = useCallback(() => {
    setBulkMode(CANVAS_ACCESS_BULK_MODE.IDLE);
    setSelectedLocationKeys([]);
    setBulkRemoveOpen(false);
  }, []);

  const clearSelection = useCallback(() => {
    setSelectedLocationKeys([]);
    setBulkRemoveOpen(false);
  }, []);

  const setRowDraftRole = useCallback((locationKey, roleId) => {
    setDraftRoleByLocation((prev) => ({
      ...prev,
      [locationKey]: normalizeCanvasRoleId(roleId),
    }));
  }, []);

  const getRowDraftRole = useCallback(
    (locationKey) => draftRoleByLocation[locationKey],
    [draftRoleByLocation],
  );

  const isLocationSelected = useCallback(
    (locationKey) => selectedLocationKeys.includes(locationKey),
    [selectedLocationKeys],
  );

  const bulkSameRoleDraftId = draftRoleByLocation[CANVAS_ACCESS_BULK_SAME_ROLE_DRAFT_KEY] ?? '';

  const isBulkSaveDisabled = useMemo(() => {
    if (bulkMode === CANVAS_ACCESS_BULK_MODE.SAME_ROLE) {
      return commonRoleIntersection.length === 0 || !bulkSameRoleDraftId;
    }

    if (bulkMode === CANVAS_ACCESS_BULK_MODE.DIFFERENT_ROLES) {
      return !hasAllBulkDraftRoles(selectedLocationKeys, draftRoleByLocation);
    }

    return true;
  }, [
    bulkMode,
    bulkSameRoleDraftId,
    commonRoleIntersection.length,
    draftRoleByLocation,
    selectedLocationKeys,
  ]);

  const showSelectionBar =
    selectedLocationKeys.length > 0 && bulkMode === CANVAS_ACCESS_BULK_MODE.IDLE;
  const showSameRolePanel = bulkMode === CANVAS_ACCESS_BULK_MODE.SAME_ROLE;
  const showDifferentRolesPanel = bulkMode === CANVAS_ACCESS_BULK_MODE.DIFFERENT_ROLES;
  const showBulkRemoveAction = provisionedSelectedRows.length > 0;

  const bulkRemoveConfirmMessage = useMemo(
    () => buildBulkRemoveConfirmMessage({ staffName, rows: provisionedSelectedRows }),
    [provisionedSelectedRows, staffName],
  );

  return {
    bulkMode,
    bulkRemoveConfirmMessage,
    bulkRemoveOpen,
    bulkSameRoleDraftId,
    cancelBulk,
    cancelBulkRemove,
    clearBulkForRowEdit,
    clearSelection,
    commonRoleIntersection,
    confirmBulkRemove,
    draftRoleByLocation,
    getRowDraftRole,
    handleBulkDifferentRoleChange,
    handleBulkSameRoleChange,
    isBulkSaveDisabled,
    isLocationSelected,
    provisionedSelectedRows,
    requestBulkRemove,
    rolesLoading,
    saveBulk,
    selectedLocationKeys,
    selectedRows,
    setRowDraftRole,
    showBulkRemoveAction,
    showDifferentRolesPanel,
    showSameRolePanel,
    showSelectionBar,
    startBulkDifferentRoles,
    startBulkSameRole,
    toggleSelected,
  };
};

export default useStaffCanvasAccessBulkBarHook;
